import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Grid } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import SydLabel from '../../../../../../commonDesign/SydLabel'
import SydInput from '../../../../../../commonDesign/SydInput'
import SydSwitch from '../../../../../../commonDesign/SydSwitch'
import FieldTypeConfig from './FieldTypeConfig'

const EDITOR_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'number', label: 'Number' },
  { value: 'bool', label: 'Boolean' },
  { value: 'select', label: 'Select' },
  { value: 'json', label: 'JSON' },
  { value: 'date', label: 'Date' },
  { value: 'datetime', label: 'Date Time' },
  { value: 'time', label: 'Time' },
  { value: 'hidden', label: 'Hidden' },
  { value: 'readonly-description', label: 'Readonly Description' },
  { value: 'readonly-accessor', label: 'Readonly Accessor' },
  { value: 'appcontext', label: 'App Context' },
  { value: 'markdown', label: 'Markdown' }
]

const INVISIBLE_TYPES = new Set(['hidden', 'appcontext'])
const READONLY_TYPES = new Set(['readonly-description', 'readonly-accessor'])
const NO_VALIDATION_RULES_TYPES = new Set(['bool', 'select', 'hidden', 'appcontext', 'readonly-description', 'readonly-accessor'])
const NO_REQUIRED_TYPES = new Set(['bool', 'hidden', 'appcontext', 'readonly-description', 'readonly-accessor'])
const NO_LAYOUT_TYPES = new Set(['hidden', 'appcontext'])
const HAS_DESCRIPTION = new Set(['text', 'textarea', 'number', 'select', 'json', 'date', 'datetime', 'time', 'markdown'])

const DATE_RANGE_CONFIG = {
  date: { inputType: 'date', minLabel: 'Earliest Date', maxLabel: 'Latest Date' },
  datetime: { inputType: 'datetime-local', minLabel: 'Earliest Date/Time', maxLabel: 'Latest Date/Time' },
  time: { inputType: 'time', minLabel: 'Earliest Time', maxLabel: 'Latest Time' }
}

const useStyles = makeStyles(() => ({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    paddingBottom: '12px',
    borderBottom: '1px solid var(--color-neutral-150)'
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0
  },
  headerTitle: {
    fontWeight: 600,
    fontSize: '14px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  headerBadge: {
    fontSize: '10px',
    color: 'var(--color-primary-600)',
    backgroundColor: 'var(--color-primary-050)',
    padding: '1px 8px',
    borderRadius: '10px',
    fontWeight: 600,
    flexShrink: 0
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    flexShrink: 0
  },
  iconBtn: {
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    color: 'var(--text-color-subtle)',
    fontSize: '12px',
    '&:hover': {
      backgroundColor: 'var(--color-neutral-100)',
      color: 'var(--text-color-primary)'
    },
    '&:disabled': {
      opacity: 0.25,
      cursor: 'default',
      '&:hover': {
        backgroundColor: 'transparent',
        color: 'var(--text-color-subtle)'
      }
    }
  },
  deleteBtn: {
    '&:hover': {
      backgroundColor: 'var(--color-danger-050)',
      color: 'var(--color-danger-500)'
    }
  },
  section: {
    marginBottom: '16px'
  },
  sectionLabel: {
    fontSize: '10px',
    fontWeight: 700,
    color: 'var(--text-color-subtle)',
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    marginBottom: '8px'
  },
  selectInput: {
    width: '100%',
    height: '54px',
    padding: '9px 15px',
    boxSizing: 'border-box',
    borderRadius: 'var(--border-radius-sm)',
    border: 'var(--border-neutral-thin)',
    backgroundColor: 'var(--color-surface-050)',
    color: 'var(--text-color-main)',
    fontSize: 'var(--font-size-sm)',
    fontFamily: 'var(--root-font)',
    '&:focus': {
      outline: 'none',
      borderColor: 'var(--color-primary-400)'
    }
  },
  divider: {
    height: '1px',
    backgroundColor: 'var(--color-neutral-100)',
    margin: '16px 0'
  }
}))

function FieldEditor ({ field, fieldEntries, onChange, onDelete, onMove, onDuplicate }) {
  const classes = useStyles()
  const editorType = field.editorType || 'text'

  const fieldIndex = useMemo(() => {
    if (!fieldEntries) return -1
    return fieldEntries.findIndex(([id]) => id === field.id)
  }, [fieldEntries, field.id])

  const isFirst = fieldIndex <= 0
  const isLast = !fieldEntries || fieldIndex >= fieldEntries.length - 1

  const showDescription = HAS_DESCRIPTION.has(editorType)
  const showLayout = !NO_LAYOUT_TYPES.has(editorType)
  const showValidationRules = !NO_VALIDATION_RULES_TYPES.has(editorType)
  const isInvisible = INVISIBLE_TYPES.has(editorType)
  const isReadonly = READONLY_TYPES.has(editorType)
  const showRequired = !NO_REQUIRED_TYPES.has(editorType)
  const showDefaultValue = !isReadonly && editorType !== 'appcontext'
  const showInitFrom = !isReadonly && !INVISIBLE_TYPES.has(editorType)

  const update = useCallback((key, value) => {
    onChange({ ...field, [key]: value })
  }, [field, onChange])

  const updateNested = useCallback((path, value) => {
    const keys = path.split('.')
    const result = { ...field }
    let current = result
    for (let i = 0; i < keys.length - 1; i++) {
      current[keys[i]] = { ...(current[keys[i]] || {}) }
      current = current[keys[i]]
    }
    current[keys[keys.length - 1]] = value
    onChange(result)
  }, [field, onChange])

  const updateValidationRule = useCallback((rule, value) => {
    const rules = { ...(field.validationRules || {}) }
    if (value === '' || value === null || value === undefined) {
      delete rules[rule]
    } else {
      rules[rule] = value
    }
    update('validationRules', rules)
  }, [field, update])

  return (
    <div>
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <span className={classes.headerTitle}>{field.label || field.id || 'Field'}</span>
          <span className={classes.headerBadge}>{editorType}</span>
        </div>
        <div className={classes.headerActions}>
          <button type='button' className={classes.iconBtn} disabled={isFirst} onClick={() => onMove(-1)} title='Move up'>&#9650;</button>
          <button type='button' className={classes.iconBtn} disabled={isLast} onClick={() => onMove(1)} title='Move down'>&#9660;</button>
          <button type='button' className={classes.iconBtn} onClick={onDuplicate} title='Duplicate field'>&#9112;</button>
          <button type='button' className={`${classes.iconBtn} ${classes.deleteBtn}`} onClick={onDelete} title='Delete field'>&#10005;</button>
        </div>
      </div>

      {/* PROPERTIES — always shown */}
      <div className={classes.section}>
        <div className={classes.sectionLabel}>Properties</div>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <SydLabel label='Field ID'>
              <SydInput value={field.id || ''} onChange={(e) => update('id', e.target.value)} />
            </SydLabel>
          </Grid>
          <Grid item xs={6}>
            <SydLabel label='Type'>
              <select value={editorType} onChange={(e) => update('editorType', e.target.value)} className={classes.selectInput}>
                {EDITOR_TYPES.map(et => (
                  <option key={et.value} value={et.value}>{et.label}</option>
                ))}
              </select>
            </SydLabel>
          </Grid>
          {!isInvisible && !isReadonly && (
            <Grid item xs={6}>
              <SydLabel label='Label'>
                <SydInput value={field.label || ''} onChange={(e) => update('label', e.target.value)} />
              </SydLabel>
            </Grid>
          )}
          {showDescription && (
            <Grid item xs={6}>
              <SydLabel label='Description'>
                <SydInput value={field.description || ''} onChange={(e) => update('description', e.target.value)} placeholder='Help text shown to user' />
              </SydLabel>
            </Grid>
          )}
        </Grid>
      </div>

      {/* LAYOUT & BEHAVIOR — not for hidden/appcontext */}
      {(showLayout || showDefaultValue || showInitFrom) && (
        <>
          <div className={classes.divider} />
          <div className={classes.section}>
            <div className={classes.sectionLabel}>
              {isReadonly ? 'Behavior' : 'Layout & Behavior'}
            </div>
            <Grid container spacing={1}>
              {showLayout && !isReadonly && (
                <Grid item xs={3}>
                  <SydLabel label='Width (cols)'>
                    <SydInput
                      type='number'
                      value={field.display?.size?.xs ?? 12}
                      onChange={(e) => updateNested('display.size.xs', parseInt(e.target.value) || 12)}
                    />
                  </SydLabel>
                </Grid>
              )}
              {showRequired && (
                <Grid item xs={3}>
                  <SydLabel label='Required'>
                    <div style={{ paddingTop: '6px' }}>
                      <SydSwitch value={!!field.required} onChange={(val) => update('required', val)} />
                    </div>
                  </SydLabel>
                </Grid>
              )}
              {showDefaultValue && (
                <Grid item xs={3}>
                  <SydLabel label='Default'>
                    <SydInput value={field.defaultValue ?? ''} onChange={(e) => update('defaultValue', e.target.value)} />
                  </SydLabel>
                </Grid>
              )}
              {showInitFrom && (
                <Grid item xs={3}>
                  <SydLabel label='Init From'>
                    <SydInput value={field.initFrom || ''} onChange={(e) => update('initFrom', e.target.value)} placeholder='$.path' />
                  </SydLabel>
                </Grid>
              )}
            </Grid>
          </div>
        </>
      )}

      {/* VALIDATION — only for input types that wire validation rules */}
      {showValidationRules && (
        <>
          <div className={classes.divider} />
          <div className={classes.section}>
            <div className={classes.sectionLabel}>Validation</div>
            <Grid container spacing={1}>
              {editorType === 'number' && (
                <>
                  <Grid item xs={3}>
                    <SydLabel label='Min'>
                      <SydInput type='number' value={field.validationRules?.min ?? ''} onChange={(e) => updateValidationRule('min', e.target.value ? Number(e.target.value) : null)} />
                    </SydLabel>
                  </Grid>
                  <Grid item xs={3}>
                    <SydLabel label='Max'>
                      <SydInput type='number' value={field.validationRules?.max ?? ''} onChange={(e) => updateValidationRule('max', e.target.value ? Number(e.target.value) : null)} />
                    </SydLabel>
                  </Grid>
                </>
              )}
              {DATE_RANGE_CONFIG[editorType] && (
                <>
                  <Grid item xs={4}>
                    <SydLabel label={DATE_RANGE_CONFIG[editorType].minLabel}>
                      <input type={DATE_RANGE_CONFIG[editorType].inputType} className={classes.selectInput} value={field.validationRules?.min ?? ''} onChange={(e) => updateValidationRule('min', e.target.value || null)} />
                    </SydLabel>
                  </Grid>
                  <Grid item xs={4}>
                    <SydLabel label={DATE_RANGE_CONFIG[editorType].maxLabel}>
                      <input type={DATE_RANGE_CONFIG[editorType].inputType} className={classes.selectInput} value={field.validationRules?.max ?? ''} onChange={(e) => updateValidationRule('max', e.target.value || null)} />
                    </SydLabel>
                  </Grid>
                </>
              )}
              {(editorType === 'text' || editorType === 'textarea' || editorType === 'json' || editorType === 'markdown') && (
                <>
                  <Grid item xs={3}>
                    <SydLabel label='Min Length'>
                      <SydInput type='number' value={field.validationRules?.minLength ?? ''} onChange={(e) => updateValidationRule('minLength', e.target.value ? Number(e.target.value) : null)} />
                    </SydLabel>
                  </Grid>
                  <Grid item xs={3}>
                    <SydLabel label='Max Length'>
                      <SydInput type='number' value={field.validationRules?.maxLength ?? ''} onChange={(e) => updateValidationRule('maxLength', e.target.value ? Number(e.target.value) : null)} />
                    </SydLabel>
                  </Grid>
                </>
              )}
              {(editorType === 'text' || editorType === 'textarea' || editorType === 'markdown') && (
                <Grid item xs={6}>
                  <SydLabel label='Pattern'>
                    <SydInput value={field.validationRules?.pattern ?? ''} onChange={(e) => updateValidationRule('pattern', e.target.value || null)} placeholder='Regex pattern' />
                  </SydLabel>
                </Grid>
              )}
            </Grid>
          </div>
        </>
      )}

      {/* TYPE-SPECIFIC CONFIG */}
      <FieldTypeConfig
        field={field}
        onChange={onChange}
        sectionClass={classes.section}
        sectionLabelClass={classes.sectionLabel}
        dividerClass={classes.divider}
      />
    </div>
  )
}

FieldEditor.propTypes = {
  field: PropTypes.object.isRequired,
  fieldEntries: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func
}

export default FieldEditor
